import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Loader2, RefreshCw, Calendar, CheckSquare, Building2, Briefcase, AlertCircle } from 'lucide-react';
import type { Team, Task, TeamMember, TaskFilters } from '../../types';
import { getTasks, markTaskDone, markTaskOpen } from '../../services/tasks';
import { getTeamMembers } from '../../services/teams';
import { TASK_PRIORITIES } from '../../constants';

interface TasksPageProps {
  team: Team;
  userId: string;
  isAdmin?: boolean;
}

const tabs = [
  { key: 'overdue', label: 'Overdue', icon: AlertCircle },
  { key: 'today', label: 'Today', icon: Calendar },
  { key: 'next7days', label: 'Next 7 Days', icon: Calendar },
  { key: 'done', label: 'Done', icon: CheckSquare },
] as const;

const formatDueDate = (dateStr: string): string => {
  const date = new Date(dateStr);
  const now = new Date();
  const diffMs = date.getTime() - now.getTime();
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

  if (diffDays < -1) return `${Math.abs(diffDays)} days overdue`;
  if (diffDays === -1) return 'Yesterday';
  if (diffDays === 0) {
    return date.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });
  }
  if (diffDays === 1) return 'Tomorrow';
  return date.toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short' });
};

const getPriorityColour = (priority: string): string => {
  const config = TASK_PRIORITIES.find((p) => p.value === priority);
  return config?.colour || 'bg-slate-500';
};

const TasksPage: React.FC<TasksPageProps> = ({ team, userId, isAdmin }) => {
  const [tasks, setTasks] = useState<Task[]>([]);
  const [members, setMembers] = useState<TeamMember[]>([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState<TaskFilters>({
    owner: '',
    tab: 'today',
    showAll: false,
  });

  const fetchData = useCallback(async () => {
    setLoading(true);
    const [tasksData, membersData] = await Promise.all([
      getTasks(team.id, { allTeams: isAdmin }),
      getTeamMembers(team.id),
    ]);
    setTasks(tasksData);
    setMembers(membersData);
    setLoading(false);
  }, [team.id, isAdmin]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const categorisedTasks = useMemo(() => {
    const now = new Date();
    const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const endOfToday = new Date(startOfToday.getTime() + 24 * 60 * 60 * 1000);
    const endOf7Days = new Date(startOfToday.getTime() + 7 * 24 * 60 * 60 * 1000);

    const filteredByOwner = filters.showAll
      ? tasks
      : tasks.filter((t) => t.owner_user_id === userId);

    const overdue: Task[] = [];
    const today: Task[] = [];
    const next7days: Task[] = [];
    const done: Task[] = [];

    filteredByOwner.forEach((task) => {
      if (task.status === 'done') {
        done.push(task);
        return;
      }

      const dueAt = new Date(task.due_at);

      if (dueAt < startOfToday) {
        overdue.push(task);
      } else if (dueAt >= startOfToday && dueAt < endOfToday) {
        today.push(task);
      } else if (dueAt >= startOfToday && dueAt < endOf7Days) {
        next7days.push(task);
      }
    });

    return { overdue, today, next7days, done };
  }, [tasks, userId, filters.showAll]);

  const currentTasks = categorisedTasks[filters.tab];

  const handleToggleTask = async (task: Task) => {
    const newStatus = task.status === 'done' ? 'open' : 'done';
    setTasks(tasks.map((t) => (t.id === task.id ? { ...t, status: newStatus as 'open' | 'done' } : t)));

    const success = newStatus === 'done' ? await markTaskDone(task.id) : await markTaskOpen(task.id);
    if (!success) {
      setTasks(tasks.map((t) => (t.id === task.id ? { ...t, status: task.status } : t)));
    }
  };

  const getOwnerEmail = (userId: string | null) => {
    if (!userId) return '';
    const member = members.find((m) => m.user_id === userId);
    return member?.email?.split('@')[0] || '';
  };

  return (
    <div className="animate-fadeIn">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-white">Tasks</h1>
          <p className="text-slate-400 text-sm mt-1">
            {categorisedTasks.overdue.length} overdue · {categorisedTasks.today.length} today
          </p>
        </div>
        <div className="flex items-center gap-3">
          <label className="flex items-center gap-2 text-sm text-slate-400">
            <input
              type="checkbox"
              checked={filters.showAll}
              onChange={(e) => setFilters({ ...filters, showAll: e.target.checked })}
              className="w-4 h-4 rounded border-slate-600 bg-slate-800 text-orange-500 focus:ring-orange-500"
            />
            Show all tasks
          </label>
          <button
            onClick={fetchData}
            disabled={loading}
            className="p-2 text-slate-400 hover:text-white hover:bg-slate-800 rounded-lg transition-colors"
          >
            <RefreshCw size={18} className={loading ? 'animate-spin' : ''} />
          </button>
        </div>
      </div>

      <div className="bg-slate-900 rounded-xl border border-slate-800">
        {/* Tabs */}
        <div className="flex border-b border-slate-800">
          {tabs.map(({ key, label, icon: Icon }) => {
            const count = categorisedTasks[key].length;
            const isActive = filters.tab === key;

            return (
              <button
                key={key}
                onClick={() => setFilters({ ...filters, tab: key })}
                className={`flex items-center gap-2 px-4 py-3 text-sm font-medium transition-colors border-b-2 -mb-px ${
                  isActive
                    ? 'text-orange-400 border-orange-500'
                    : 'text-slate-400 border-transparent hover:text-white'
                }`}
              >
                <Icon size={16} />
                {label}
                <span className={`px-1.5 py-0.5 rounded text-xs ${
                  isActive ? 'bg-orange-500/20 text-orange-400' : 'bg-slate-800 text-slate-500'
                }`}>
                  {count}
                </span>
              </button>
            );
          })}
        </div>

        {/* Task List */}
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 size={24} className="animate-spin text-orange-500" />
          </div>
        ) : currentTasks.length === 0 ? (
          <div className="text-center py-12 text-slate-400">
            <CheckSquare size={48} className="mx-auto mb-4 opacity-50" />
            <p>No tasks in this category</p>
          </div>
        ) : (
          <div className="divide-y divide-slate-800">
            {currentTasks.map((task) => (
              <div
                key={task.id}
                className="flex items-center gap-4 p-4 hover:bg-slate-800/50 transition-colors"
              >
                <button
                  onClick={() => handleToggleTask(task)}
                  className={`w-5 h-5 rounded border flex items-center justify-center transition-colors shrink-0 ${
                    task.status === 'done'
                      ? 'bg-emerald-500 border-emerald-500 text-white'
                      : 'border-slate-600 hover:border-slate-500'
                  }`}
                >
                  {task.status === 'done' && <span className="text-xs">✓</span>}
                </button>

                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <p className={`font-medium ${task.status === 'done' ? 'text-slate-500 line-through' : 'text-white'}`}>
                      {task.title}
                    </p>
                    <span className={`w-2 h-2 rounded-full ${getPriorityColour(task.priority)}`} />
                  </div>
                  <div className="flex items-center gap-4 mt-1 text-sm text-slate-500">
                    {task.contact && (
                      <span className="flex items-center gap-1">
                        <Building2 size={12} />
                        {task.contact.company || `${task.contact.first_name} ${task.contact.last_name}`}
                      </span>
                    )}
                    {task.deal && (
                      <span className="flex items-center gap-1">
                        <Briefcase size={12} />
                        {task.deal.title}
                      </span>
                    )}
                  </div>
                </div>

                <div className="text-right shrink-0">
                  <p className={`text-sm ${
                    filters.tab === 'overdue' ? 'text-red-400' :
                    filters.tab === 'today' ? 'text-amber-400' : 'text-slate-400'
                  }`}>
                    {formatDueDate(task.due_at)}
                  </p>
                  {task.owner_user_id && (
                    <p className="text-xs text-slate-500 mt-0.5">
                      {getOwnerEmail(task.owner_user_id)}
                    </p>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default TasksPage;
